import { CommonModule } from '@angular/common';
import { Component, ViewChild, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Country } from '../../../models/country';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { ColumnConfig, LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';
import { forkJoin } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { REGIONS } from '../../../utilities/regions';

@Component({
  selector: 'app-country-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './country-list.component.html',
  styleUrl: './country-list.component.scss'
})
export class CountryListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  rightItem: Country | undefined = undefined;
  matchedItems: Country[] | undefined = undefined;

  countries: Country[] = [];
  enableMatching = false;

  nationalities: any[] = [];
  currencies: any[] = [];
  stockExchanges: any[] = [];

  countriesColumns: ColumnConfig[] = [];

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private spinnerService: NgxSpinnerService,
    private lutService: LutService,
    private lookupDataService: LookupDataService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadLookupData();
  }

  loadLookupData(): void {
    this.spinnerService.show();

    // Load all lookup data from cache
    forkJoin({
      nationalities: this.lookupDataService.getTableStream('nationalities').pipe(
        filter(data => data !== null),
        take(1)
      ),
      currencies: this.lookupDataService.getTableStream('currencies').pipe(
        filter(data => data !== null),
        take(1)
      ),
      stockExchanges: this.lookupDataService.getTableStream('stockExchanges').pipe(
        filter(data => data !== null),
        take(1)
      )
    }).subscribe({
      next: ({ nationalities, currencies, stockExchanges }) => {
        this.nationalities = nationalities;
        this.currencies = currencies;
        this.stockExchanges = stockExchanges;

        // Setup columns with loaded lookup data
        this.countriesColumns = [
          { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true },
          {
            key: 'nationality',
            header: 'Nationality',
            type: 'select',
            sortable: true,
            editable: true,
            required: true,
            selectOptions: this.nationalities.map(n => ({ value: n, label: n.name })).sort((a, b) => a.label.localeCompare(b.label))
          },
          {
            key: 'currency',
            header: 'Currency',
            type: 'select',
            sortable: true,
            editable: true,
            required: true,
            selectOptions: this.currencies.map(c => ({ value: c, label: c.name })).sort((a, b) => a.label.localeCompare(b.label))
          },
          {
            key: 'region',
            header: 'Region',
            type: 'select',
            sortable: true,
            editable: true,
            required: true,
            selectOptions: REGIONS.map(region => ({
              value: region.value,
              label: region.label
            }))
          },
          {
            key: 'stockExchange1',
            header: 'Stock Exchange 1',
            type: 'select',
            sortable: true,
            editable: true,
            selectOptions: this.stockExchanges.map(s => ({ value: s, label: s.name })).sort((a, b) => a.label.localeCompare(b.label))
          },
          {
            key: 'stockExchange2',
            header: 'Stock Exchange 2',
            type: 'select',
            sortable: true,
            editable: true,
            selectOptions: this.stockExchanges.map(s => ({ value: s, label: s.name })).sort((a, b) => a.label.localeCompare(b.label))
          },
          { key: 'isVisible', header: 'Visible', type: 'boolean', sortable: true, editable: true }
        ];

        // Now load countries
        this.loadCountries();
      },
      error: (error) => {
        console.error('Error loading lookup data:', error);
        this.spinnerService.hide();
        const errorMessage = Helpers.parseApiError(error);
        this.toast.error(`<strong>Failed to load Lookup Data:</strong></br></br> ${errorMessage}`);
      }
    });
  }

  loadCountries(): void {
    this.lutService.getCountries().subscribe({
      next: (data) => {
        // Convert flat API response to hierarchical structure
        this.countries = Helpers.convertCountryFlatToCountry(data);
        // Sort countries alphabetically by name
        this.countries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading countries:', error);
        this.spinnerService.hide();
        const errorMessage = Helpers.parseApiError(error);
        this.toast.error(`<strong>Failed to load Countries:</strong></br></br> ${errorMessage}`);
      }
    });
  }

  onCreateCountry(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(country: Country): void {
  }

  openDeleteConfirmation(country: Country): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(country.name)
      .subscribe(result => {
        if (result) {
          // Proceed with delete operation
        }
      });
  }

  onEditCountry(country: Country): void {
  }

  onDeleteCountry(country: Country): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(country.name)
      .subscribe(result => {
        if (result) {
          // Store original array for rollback in case of error
          const originalCountries = [...this.countries];

          // 1. Optimistically remove from local state immediately
          this.countries = this.countries.filter(c => c.id !== country.id);

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteCountry(country.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Country deleted successfully');
              // Update the lookup data cache to make it available in other components
              this.lookupDataService.updateTableData('countries', this.countries.map(c => Helpers.convertCountryToCountryFlat(c)));
            },
            error: (error) => {
              console.error('Error deleting country:', error);
              this.spinnerService.hide();
              // Restore original array on error
              this.countries = originalCountries;
              const errorMessage = Helpers.parseApiError(error);
              this.toast.error(`<strong>Failed to delete Country:</strong></br></br> ${errorMessage}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: Country, newName: string} | {item: Country, newValue: any, columnKey: string}): void {
    const oldCountry = { ...event.item };
    let updatedCountry: Country;

    // Handle both legacy and new format
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedCountry = { ...event.item, name: event.newName };
      this.countries = this.countries.map(c => c.id === event.item.id ? updatedCountry : c);
    } else {
      // New format - update specific column
      updatedCountry = { ...event.item, [event.columnKey]: event.newValue };
      this.countries = this.countries.map(c => c.id === event.item.id ? updatedCountry : c);
    }

    // Call backend API
    this.spinnerService.show();
    const flatCountry = Helpers.convertCountryToCountryFlat(updatedCountry);
    this.lutService.updateCountry(event.item.id, flatCountry).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Country updated successfully');
        // Update with server response
        const updatedFromServer = Helpers.convertCountryFlatToCountry([response])[0];
        this.countries = this.countries.map(c => c.id === event.item.id ? updatedFromServer : c);
        // Update the lookup data cache to make it available in other components
        this.lookupDataService.updateTableData('countries', this.countries.map(c => Helpers.convertCountryToCountryFlat(c)));
      },
      error: (error) => {
        console.error('Error updating country:', error);
        this.spinnerService.hide();
        // Rollback on error
        this.countries = this.countries.map(c => c.id === event.item.id ? oldCountry : c);
        const errorMessage = Helpers.parseApiError(error);
        this.toast.error(`<strong>Failed to update Country:</strong></br></br> ${errorMessage}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newCountry: Country = {
      id: tempId,
      name: event['name'] || '',
      code: event['code'] || '',
      region: event['region'],
      nationality: event['nationality'] || null,
      currency: event['currency'] || null,
      stockExchange1: event['stockExchange1'] || null,
      stockExchange2: event['stockExchange2'] || null,
      isVisible: event['isVisible'] ?? false,
      systemReserved: false
    };

    // Optimistically add to local array
    this.countries.push(newCountry);

    // Call backend API
    this.spinnerService.show();
    const flatCountry = Helpers.convertCountryToCountryFlat(newCountry);
    const { id, ...createData } = flatCountry; // Remove temp ID for creation

    this.lutService.createCountry(createData).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Country created successfully');
        // Replace temp item with server response
        const createdCountry = Helpers.convertCountryFlatToCountry([response])[0];
        this.countries = this.countries.map(c => c.id === tempId ? createdCountry : c);
        // Update the lookup data cache to make it available in other components
        this.lookupDataService.updateTableData('countries', this.countries.map(c => Helpers.convertCountryToCountryFlat(c)));
      },
      error: (error) => {
        console.error('Error creating country:', error);
        this.spinnerService.hide();
        // Remove temp item on error
        this.countries = this.countries.filter(c => c.id !== tempId);
        const errorMessage = Helpers.parseApiError(error);
        this.toast.error(`<strong>Failed to create Country:</strong></br></br> ${errorMessage}`);
      }
    });
  }

  onCountryCorrected(): void {
  }
}
